import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, ShoppingBag } from "lucide-react";
import { api } from "@/api/client";
import { AuthImage } from "@/components/AuthImage";
import { SidesPicker } from "@/components/SidesPicker";
import { useCart } from "@/context/CartContext";
import { useCartTransition } from "@/context/CartTransition";
import { resolveUnitPrice, isQuoteSize, money } from "@/lib/pricing";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const { triggerCartTransition } = useCartTransition();
  const [product, setProduct] = useState(null);
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(null);
  const [flavor, setFlavor] = useState(null);
  const [qty, setQty] = useState(1);
  const [message, setMessage] = useState("");
  const [sideOptions, setSideOptions] = useState([]);
  const [selectedSides, setSelectedSides] = useState([]);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .getProduct(id)
      .then(async (p) => {
        setProduct(p);
        if (p.pricing_mode === "sizes" && p.price_sizes?.length) setSize(p.price_sizes[0].name);
        else if (p.sizes?.length) setSize(p.sizes[0]);
        if (p.flavors?.length) setFlavor(p.flavors[0]);
        if (p.sides_config?.enabled) {
          api.getSideOptions(p.id).then(setSideOptions).catch(() => setSideOptions([]));
        }
        api.listBusinesses().then((all) => {
          const b = (all || []).find((x) => x.id === p.business_id);
          setPaused(!!b?.ordering_paused);
        }).catch(() => {});
        try {
          const specials = await api.listSpecials(p.business_id, { active_only: true });
          const s = specials.find((x) => x.product_id === p.id && x.sale_price != null);
          if (s && (p.pricing_mode || "single") === "single") setSale(s);
        } catch { /* ignore */ }
      })
      .catch(() => toast.error("Product not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const isQuote = useMemo(() => isQuoteSize(product, size), [product, size]);

  const sidesTotal = useMemo(
    () => (selectedSides || []).reduce((s, x) => s + (Number(x.price) || 0), 0),
    [selectedSides]
  );

  const unitPrice = useMemo(() => {
    if (!product) return 0;
    if (isQuoteSize(product, size)) return 0;
    let base = resolveUnitPrice(product, size, qty);
    if (sale && (product.pricing_mode || "single") === "single" && sale.sale_price < base) {
      base = sale.sale_price;
    }
    return base;
  }, [product, size, qty, sale]);

  const sidesEnabled = !!product?.sides_config?.enabled;
  const requiredCount = product?.sides_config?.required_count || 0;

  const displayImage = useMemo(() => {
    if (!product) return "";
    return (
      (flavor && product.flavor_images?.[flavor]) ||
      (size && product.size_images?.[size]) ||
      product.image_url
    );
  }, [product, flavor, size]);

  const addToCart = () => {
    if (!product.available) return;
    if (paused) {
      toast.error("This business isn't taking orders right now");
      return;
    }
    if (sidesEnabled && requiredCount > 0 && selectedSides.length < requiredCount) {
      toast.error(`Please choose at least ${requiredCount} side${requiredCount > 1 ? "s" : ""}`);
      return;
    }
    add({
      product_id: product.id,
      product_name: product.name,
      image_url: displayImage,
      quantity: qty,
      size: size || undefined,
      flavor: flavor || undefined,
      message_on_cake: message.trim() || undefined,
      selected_sides: sidesEnabled ? selectedSides : [],
      sides_config: product.sides_config || null,
      unit_price: unitPrice,
      is_quote: isQuote,
      pricing_mode: product.pricing_mode || "single",
      price_tiers: product.price_tiers || [],
      business_id: product.business_id,
    });
    toast.success(isQuote ? `${product.name} added — we'll quote you` : `${product.name} added to cart`);
    triggerCartTransition();
  };

  if (loading)
    return (
      <div className="container-app py-16">
        <div className="grid lg:grid-cols-2 gap-10">
          <div className="aspect-square rounded-2xl animate-pulse" style={{ background: "var(--surface-2)" }} />
          <div className="space-y-4">
            <div className="h-8 w-2/3 rounded animate-pulse" style={{ background: "var(--surface-2)" }} />
            <div className="h-4 w-full rounded animate-pulse" style={{ background: "var(--surface-2)" }} />
          </div>
        </div>
      </div>
    );

  if (!product) return null;

  const sizeOptions =
    product.pricing_mode === "sizes" && product.price_sizes?.length
      ? product.price_sizes.map((s) => ({ name: s.name, price: s.quote ? null : s.price, quote: !!s.quote }))
      : (product.sizes || []).map((s) => ({ name: s, price: null, quote: false }));

  const cakeMsgLabel = isQuote
    ? "Tell us what you'd like (size, servings, details)"
    : product.business_id === "rubys"
    ? "Note for the cook (optional)"
    : "Message on cake (optional)";

  return (
    <div className="container-app py-8 sm:py-12">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm font-semibold mb-6" style={{ color: "var(--muted)" }}>
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        <div className="rounded-3xl overflow-hidden relative" style={{ background: "var(--surface-2)" }}>
          <AuthImage key={displayImage} src={displayImage} alt={product.name} className="w-full aspect-square object-cover variant-img-fade" />
          {sale && (
            <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold uppercase shadow" style={{ background: "var(--accent)", color: "#1a1400" }}>
              {sale.badge_text || "Special"}
            </span>
          )}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-widest mb-1" style={{ color: "var(--muted)" }}>
            {product.category}
          </div>
          <h1 className="font-display text-4xl leading-tight" style={{ color: "var(--ink)" }}>
            {product.name}
          </h1>
          <div className="mt-3 flex items-baseline gap-3">
            {isQuote ? (
              <span className="font-display text-2xl" style={{ color: "var(--brand)" }}>
                Price on request
              </span>
            ) : (
              <>
                <span className="font-display text-3xl" style={{ color: "var(--brand)" }}>
                  {money(unitPrice)}
                </span>
                {sale && sale.sale_price < product.price && (
                  <span className="text-lg line-through" style={{ color: "var(--muted)" }}>{money(product.price)}</span>
                )}
                {product.pricing_mode === "tiers" && <span className="text-sm" style={{ color: "var(--muted)" }}>per unit</span>}
              </>
            )}
          </div>
          {isQuote && (
            <p className="mt-2 text-sm rounded-xl p-3" style={{ background: "var(--accent-soft)", color: "var(--ink)" }}>
              This is a custom &ldquo;Etc.&rdquo; order. Tell us what you&apos;d like below — the baker will review and contact you with a price. No deposit is charged until you agree on the quote.
            </p>
          )}
          <p className="mt-4 leading-relaxed" style={{ color: "var(--muted)" }}>
            {product.description}
          </p>

          {product.pricing_mode === "tiers" && product.price_tiers?.length > 0 && (
            <div className="mt-5 rounded-xl p-4" style={{ background: "var(--surface-2)" }}>
              <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--muted)" }}>Volume pricing</div>
              <div className="grid grid-cols-2 gap-1.5 text-sm">
                {product.price_tiers.map((t, i) => (
                  <div key={i} style={{ color: "var(--ink)" }}>
                    {t.min_qty}
                    {t.max_qty ? `–${t.max_qty}` : "+"} · {money(t.unit_price)}/ea
                  </div>
                ))}
              </div>
            </div>
          )}

          {sizeOptions.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-semibold mb-2" style={{ color: "var(--ink)" }}>Size</div>
              <div className="flex flex-wrap gap-2">
                {sizeOptions.map((s) => (
                  <button
                    key={s.name}
                    onClick={() => setSize(s.name)}
                    data-testid={`size-${s.name}`}
                    className="px-4 py-2 rounded-full text-sm font-semibold transition"
                    style={
                      size === s.name
                        ? { background: "var(--brand)", color: "var(--brand-fg)" }
                        : { background: "var(--surface)", color: "var(--ink)", border: "1px solid var(--border)" }
                    }
                  >
                    {s.name}
                    {s.quote ? (
                      <span className="ml-1.5 opacity-70">· quote</span>
                    ) : (
                      s.price != null && <span className="ml-1.5 opacity-70">{money(s.price)}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.flavors?.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-semibold mb-2" style={{ color: "var(--ink)" }}>Flavor</div>
              <div className="flex flex-wrap gap-2">
                {product.flavors.map((f) => (
                  <button
                    key={f}
                    onClick={() => setFlavor(f)}
                    data-testid={`flavor-${f}`}
                    className="px-4 py-2 rounded-full text-sm font-semibold transition"
                    style={
                      flavor === f
                        ? { background: "var(--brand)", color: "var(--brand-fg)" }
                        : { background: "var(--surface)", color: "var(--ink)", border: "1px solid var(--border)" }
                    }
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
          )}

          {sidesEnabled && (
            <div className="mt-6" data-testid="sides-section">
              <SidesPicker
                options={sideOptions}
                value={selectedSides}
                onChange={setSelectedSides}
                requiredCount={requiredCount}
              />
            </div>
          )}

          <div className="mt-6">
            <div className="text-sm font-semibold mb-2" style={{ color: "var(--ink)" }}>{cakeMsgLabel}</div>
            <input
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="message-input"
              maxLength={200}
              placeholder={isQuote ? "e.g. 3-tier wedding cake, ~60 servings, gold theme" : "e.g. Happy Birthday!"}
              className="w-full rounded-xl px-4 py-3 outline-none"
              style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
            />
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center rounded-full" style={{ border: "1px solid var(--border)", background: "var(--surface)" }}>
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease" className="p-3" aria-label="Decrease">
                <Minus size={16} />
              </button>
              <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} data-testid="qty-increase" className="p-3" aria-label="Increase">
                <Plus size={16} />
              </button>
            </div>
            <div className="text-sm" style={{ color: "var(--muted)" }}>
              {isQuote ? (
                <span className="font-display text-lg" style={{ color: "var(--ink)" }}>Price after review</span>
              ) : (
                <>Subtotal <span className="font-display text-lg ml-1" style={{ color: "var(--ink)" }}>{money((unitPrice + sidesTotal) * qty)}</span></>
              )}
            </div>
          </div>

          <button
            onClick={addToCart}
            disabled={!product.available || paused}
            data-testid="add-to-cart-btn"
            className="btn-brand w-full mt-6 rounded-xl py-3.5 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <ShoppingBag size={18} /> {!product.available ? "Sold out" : paused ? "Not taking orders" : isQuote ? "Request a quote" : "Add to cart"}
          </button>

          <p className="mt-3 text-xs text-center" style={{ color: "var(--muted)" }}>
            Reserve with a small deposit now · pay the balance when it&apos;s ready.{" "}
            <Link to="/about" className="underline">Learn more</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
